import { useRef, useCallback } from "react";

const DEFAULT_MAX_SIZE = 100;

class LRUCache<K, V> {
  private cache: Map<K, V>;
  private maxSize: number;

  constructor(maxSize: number = DEFAULT_MAX_SIZE) {
    this.cache = new Map();
    this.maxSize = maxSize;
  }

  get(key: K): V | null {
    if (!this.cache.has(key)) return null;

    const value = this.cache.get(key)!;
    this.cache.delete(key);
    this.cache.set(key, value);
    return value;
  }

  set(key: K, value: V): void {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== undefined) {
        this.cache.delete(firstKey);
      }
    }
    this.cache.set(key, value);
  }

  has(key: K): boolean {
    return this.cache.has(key);
  }

  delete(key: K): boolean {
    return this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }

  get size(): number {
    return this.cache.size;
  }
}

export type ArticleCacheEntry = {
  html: string;
  articleId: number;
  articleTitle: string;
};

export function useArticleCache(maxSize: number = DEFAULT_MAX_SIZE) {
  const cacheRef = useRef<LRUCache<number, ArticleCacheEntry>>(
    new LRUCache(maxSize),
  );

  const get = useCallback((articleId: number): ArticleCacheEntry | null => {
    return cacheRef.current.get(articleId);
  }, []);

  const set = useCallback(
    (articleId: number, entry: ArticleCacheEntry): void => {
      cacheRef.current.set(articleId, entry);
    },
    [],
  );

  const has = useCallback((articleId: number): boolean => {
    return cacheRef.current.has(articleId);
  }, []);

  const remove = useCallback((articleId: number): boolean => {
    return cacheRef.current.delete(articleId);
  }, []);

  const clear = useCallback((): void => {
    cacheRef.current.clear();
  }, []);

  const size = useCallback((): number => {
    return cacheRef.current.size;
  }, []);

  return {
    get,
    set,
    has,
    remove,
    clear,
    size,
  };
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest;

  describe("useArticleCache exports", () => {
    it("exports the hook function", () => {
      expect(useArticleCache).toBeDefined();
      expect(typeof useArticleCache).toBe("function");
    });
  });
}
